import { TableRow } from '@fluentui/react-components';
import type { HeaderGroup } from '@tanstack/react-table';
import { type Dispatch, Fragment, type SetStateAction, useMemo } from 'react';
import { ResizableTableCellHeader } from '../../_shared/cell-header/index.js';
import { ResizableTableDividerBottom } from '../../_shared/divider-bottom/index.js';
import { useStyles } from './styles.js';

type ResizableTableRowHeaderProps<TData, TColumnSizes extends Record<string, number>> = {
    headerGroups: HeaderGroup<TData>[];
    columnSizes: TColumnSizes;
    backgroundColor: string;
    setColumnSizes: Dispatch<SetStateAction<TColumnSizes>>;
    padContainerValue?: number | string;
};

export function ResizableTableRowHeader<TData, TColumnSizes extends Record<string, number>>({
    headerGroups,
    columnSizes,
    backgroundColor,
    setColumnSizes,
    padContainerValue,
}: ResizableTableRowHeaderProps<TData, TColumnSizes>) {
    const styles = useStyles();

    // memoized `onResize()` functions
    const memoizedOnResizeFunctions = useMemo(() => {
        const onResizeFunctions: Record<string, Record<string, (size: number) => void>> = {};

        headerGroups.forEach((headerGroup) => {
            // initialize the onResizeFunctions for the headerGroup if undefined
            onResizeFunctions[headerGroup.id] ??= {};

            headerGroup.headers.forEach((header) => {
                // initialize the onResizeFunctions for the header if undefined
                onResizeFunctions[headerGroup.id][header.id] ??= (size: number) => {
                    setColumnSizes((prevValue) => {
                        return {
                            ...prevValue,
                            [header.id]: size,
                        };
                    });
                };
            });
        });

        return onResizeFunctions;
    }, [headerGroups, setColumnSizes]);

    return headerGroups.map((headerGroup, headerGroupIndex) => {
        return (
            <Fragment key={headerGroup.id}>
                <TableRow
                    className={styles.rowHeader}
                    style={{
                        backgroundColor: backgroundColor,
                        paddingInline: padContainerValue,
                    }}
                >
                    {headerGroup.headers.map((header, headerIndex) => {
                        const isFirstColumn = headerGroupIndex === 0 && headerIndex === 0;
                        const isLastColumn =
                            headerGroupIndex === headerGroups.length - 1 &&
                            headerIndex === headerGroup.headers.length - 1;

                        return (
                            <ResizableTableCellHeader
                                key={header.id}
                                defaultSize={columnSizes[header.id]}
                                header={header}
                                onResize={memoizedOnResizeFunctions[headerGroup.id][header.id]}
                                padContainerValue={padContainerValue}
                                position={isFirstColumn ? 'first' : isLastColumn ? 'last' : undefined}
                                isLastColumn={headerIndex === headerGroup.headers.length - 1}
                            />
                        );
                    })}
                </TableRow>

                <ResizableTableDividerBottom size={padContainerValue ?? 0} />
            </Fragment>
        );
    });
}
